import React, { useState } from 'react';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  LockClosedIcon,
  EyeSlashIcon,
  XMarkIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { useBillingStore } from '../../store/billingStore';
import { useBilling } from '../../hooks/useBilling';
import type { LineItem, CreateLineItemDto, LineItemActivityType } from '../../types/billing.types';
import UtbmsCodeSelector from '../UtbmsCodeSelector';
import ActivityCategorySelector from '../ActivityCategorySelector';

interface Props {
  billId: string;
  isEditable: boolean;
}

const ACTIVITY_TYPE_LABELS: Record<LineItemActivityType, string> = {
  time_entry: 'Time Entry',
  expense_entry: 'Expense',
  flat_fee: 'Flat Fee',
  manual: 'Manual',
};

const InvoiceLineItemsTab: React.FC<Props> = ({ billId, isEditable }) => {
  const lineItems = useBillingStore((s) => s.currentLineItems);
  const { addLineItem, updateLineItem, removeLineItem } = useBilling();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Add form state
  const [formType, setFormType] = useState<LineItemActivityType>('manual');
  const [formDesc, setFormDesc] = useState('');
  const [formDate, setFormDate] = useState('');
  const [formQty, setFormQty] = useState('1');
  const [formRate, setFormRate] = useState('0');
  const [formCategory, setFormCategory] = useState('');
  const [formUtbms, setFormUtbms] = useState('');
  const [formTaxable, setFormTaxable] = useState(false);
  const [formTaxRate, setFormTaxRate] = useState('0');
  const [formBillable, setFormBillable] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [descTouched, setDescTouched] = useState(false);

  const resetForm = () => {
    setFormType('manual');
    setFormDesc('');
    setFormDate('');
    setFormQty('1');
    setFormRate('0');
    setFormCategory('');
    setFormUtbms('');
    setFormTaxable(false);
    setFormTaxRate('0');
    setFormBillable(true);
    setFormError(null);
    setDescTouched(false);
  };

  const handleAdd = async () => {
    setDescTouched(true);
    if (!formDesc.trim()) {
      setFormError('Description is required.');
      return;
    }
    if (submitting) return;
    setFormError(null);
    setSubmitting(true);
    try {
      const dto: CreateLineItemDto = {
        source_activity_type: formType,
        description: formDesc,
        service_date: formDate || undefined,
        quantity: parseFloat(formQty) || 1,
        rate: parseFloat(formRate) || 0,
        category: formCategory || undefined,
        utbms_code: formUtbms || undefined,
        is_taxable: formTaxable,
        tax_rate: formTaxable ? parseFloat(formTaxRate) || 0 : undefined,
        is_billable: formBillable,
      };
      await addLineItem(billId, dto);
      resetForm();
      setShowAddForm(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add line item';
      setFormError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (lineItemId: string) => {
    try {
      await removeLineItem(billId, lineItemId);
      setDeleteConfirmId(null);
    } catch {
      // error handled by hook
    }
  };

  const handleInlineEdit = async (li: LineItem, field: string, value: unknown) => {
    try {
      await updateLineItem(billId, li.line_item_id, { [field]: value });
      setEditingId(null);
    } catch {
      // error handled by hook
    }
  };

  const fmtDate = (d: string | null | undefined) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const fmtCurrency = (v: number) => `$${Number(v || 0).toFixed(2)}`;

  // Running totals
  const totals = lineItems.reduce(
    (acc, li) => {
      if (li.is_billable && !li.is_write_off) {
        acc.subtotal += Number(li.net_amount || 0);
        acc.tax += Number(li.tax_amount || 0);
        acc.discount += Number(li.discount_amount || 0);
      }
      return acc;
    },
    { subtotal: 0, tax: 0, discount: 0 }
  );

  return (
    <div className="space-y-4">
      {/* Add button */}
      {isEditable && (
        <div className="flex justify-end">
          <button
            onClick={() => { setShowAddForm(!showAddForm); resetForm(); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Add Line Item
          </button>
        </div>
      )}

      {/* Add form */}
      {showAddForm && (
        <div className="bg-lex-accent-soft border border-lex-accent rounded-lg p-4 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Type</label>
              <select
                value={formType}
                onChange={(e) => setFormType(e.target.value as LineItemActivityType)}
                className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
              >
                <option value="manual">Manual</option>
                <option value="flat_fee">Flat Fee</option>
                <option value="time_entry">Time Entry</option>
                <option value="expense_entry">Expense</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Date</label>
              <input
                type="date"
                value={formDate}
                onChange={(e) => setFormDate(e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Quantity</label>
              <input
                type="number"
                step="0.01"
                value={formQty}
                onChange={(e) => setFormQty(e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Rate ($)</label>
              <input
                type="number"
                step="0.01"
                value={formRate}
                onChange={(e) => setFormRate(e.target.value)}
                className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description *</label>
            <input
              type="text"
              value={formDesc}
              onChange={(e) => { setFormDesc(e.target.value); setDescTouched(true); if (e.target.value.trim()) setFormError(null); }}
              placeholder="Describe the line item..."
              className={`w-full px-2 py-1.5 border rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white ${
                descTouched && !formDesc.trim()
                  ? 'border-red-400 dark:border-red-500 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            {descTouched && !formDesc.trim() && (
              <p className="mt-1 text-xs text-red-500">Description is required to add a line item.</p>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <ActivityCategorySelector
              value={formCategory}
              onChange={setFormCategory}
              onUtbmsCodeSuggested={(code) => { if (!formUtbms) setFormUtbms(code); }}
              label="Category"
              placeholder="e.g. Legal Research"
            />
            <UtbmsCodeSelector
              value={formUtbms}
              onChange={setFormUtbms}
              label="UTBMS Code"
            />
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-1.5 text-sm">
                <input
                  type="checkbox"
                  checked={formTaxable}
                  onChange={(e) => setFormTaxable(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-gray-600 dark:text-gray-400">Taxable</span>
              </label>
              <label className="flex items-center gap-1.5 text-sm">
                <input
                  type="checkbox"
                  checked={formBillable}
                  onChange={(e) => setFormBillable(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-gray-600 dark:text-gray-400">Billable</span>
              </label>
            </div>
            {formTaxable && (
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Tax Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formTaxRate}
                  onChange={(e) => setFormTaxRate(e.target.value)}
                  className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
                />
              </div>
            )}
          </div>
          {formError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg px-3 py-2">
              <p className="text-sm text-red-700 dark:text-red-300">{formError}</p>
            </div>
          )}
          <div className="flex items-center justify-between pt-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Amount: {fmtCurrency((parseFloat(formQty) || 0) * (parseFloat(formRate) || 0))}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setShowAddForm(false); resetForm(); }}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={submitting}
                className="px-3 py-1.5 text-sm font-medium bg-lex-accent text-lex-accent-contrast rounded hover:bg-lex-accent-hover disabled:opacity-50"
              >
                {submitting ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Line items table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">#</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Description</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Type</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Qty</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Rate</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Amount</th>
                {isEditable && (
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide w-20">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {lineItems.length === 0 ? (
                <tr>
                  <td colSpan={isEditable ? 8 : 7} className="px-4 py-8 text-center text-gray-400 dark:text-gray-500">
                    No line items yet. {isEditable ? 'Click "Add Line Item" to get started.' : ''}
                  </td>
                </tr>
              ) : (
                lineItems.map((li, idx) => (
                  <tr
                    key={li.line_item_id}
                    className={`${
                      li.is_write_off
                        ? 'line-through text-gray-400 dark:text-gray-500'
                        : !li.is_billable
                          ? 'text-gray-400 dark:text-gray-500'
                          : ''
                    } hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors`}
                  >
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                      <div className="flex items-center gap-1">
                        {idx + 1}
                        {li.is_locked && <LockClosedIcon className="h-3 w-3 text-gray-400" />}
                        {li.is_confidential && <EyeSlashIcon className="h-3 w-3 text-gray-400" />}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtDate(li.service_date)}</td>
                    <td className="px-4 py-3 text-gray-900 dark:text-white max-w-[300px]">
                      <div className="truncate">{li.description}</div>
                      {li.category && (
                        <div className="text-xs text-gray-400 dark:text-gray-500">{li.category}</div>
                      )}
                      {li.utbms_code && (
                        <span className="text-xs text-gray-400">[{li.utbms_code}]</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                        {ACTIVITY_TYPE_LABELS[li.source_activity_type]}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">{Number(li.quantity).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-300">{fmtCurrency(li.rate)}</td>
                    <td className="px-4 py-3 text-right font-medium text-gray-900 dark:text-white">{fmtCurrency(li.total_amount)}</td>
                    {isEditable && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {deleteConfirmId === li.line_item_id ? (
                            <>
                              <button
                                onClick={() => handleDelete(li.line_item_id)}
                                className="p-1 text-red-500 hover:text-red-700"
                                title="Confirm delete"
                              >
                                <CheckIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(null)}
                                className="p-1 text-gray-400 hover:text-gray-600"
                                title="Cancel"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirmId(li.line_item_id)}
                              disabled={li.is_locked}
                              className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Remove line item"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
            {lineItems.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-750">
                  <td colSpan={6} className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Total ({lineItems.length} items)
                  </td>
                  <td className="px-4 py-3 text-right text-sm font-bold text-gray-900 dark:text-white">
                    {fmtCurrency(totals.subtotal + totals.tax)}
                  </td>
                  {isEditable && <td />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default InvoiceLineItemsTab;
